import {useState} from 'react';

import styled from 'styled-components';
import {useDispatch} from 'react-redux';

import {FlexColumn} from '~/shared/theme/FlexLayout';
import {PieCreditCardNumberInput} from '~/shared/components/CreditCardNumberInput';
import PieButton from '~/shared/theme/pie/PieButton';
import {attachModal} from '~/shared/components/ReduxModal';
import {PieCloseModalButton} from '~/shared/components/Modals/styled';
import {flipOnLTR, media} from '~/shared/theme/utils';
import {bodyBold, headingXS, subheadingSBold} from '~/shared/theme/typography';
import {getLocalizationService} from '~/shared/services/localisationService';
import apiService from '~/shared/services/apiService';
import {is401Error} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';
import {createLogger} from '~/shared/logging';
import {setCurrentModal} from '~/shared/store/storeModules/currentModal/currentModalActions';
import {trackEvent} from '~/shared/services/analytics';
import frustratedImg from '~/assets/images/frustratedImg.png';

import activateCardSuccessImg from './activateCardSuccess.png';

const logger = createLogger('PieActivateMoneycardModal');

const Root = styled(FlexColumn)`
  position: relative;

  && {
    background-color: ${({theme}) => theme.colors.surface};
    height: 100%;
    width: 100%;
  }

  ${media.minLargeMobile`
    && {
      height: auto;
      width: 500px;
    }
  `}
`;

const Title = styled.h3`
  ${subheadingSBold}
  text-align: center;
  padding: 16px 0 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  ${media.minLargeMobile`
    ${headingXS}
  `}
`;

const Description = styled.span`
  display: block;
  ${bodyBold}
  width: 100%;
  text-align: center;
`;

const Content = styled(FlexColumn)`
  justify-content: center;
  align-items: center;
  padding: 32px 16px;
  gap: 40px;
  ${media.minLargeMobile`
    padding: 32px 40px 20px;
  `}
`;

const StyledPieCloseModalButton = styled(PieCloseModalButton)`
  top: 16px;
  ${flipOnLTR`
    left: 16px;
  `}
`;

const SubmitButton = styled(PieButton)`
  width: 100%;
`;

type PieActivateMoneycardModalProps = {
  args: {
    onSuccess?: (() => void) | (() => Promise<void>);
  };
  closeModal: () => void;
};

const PieActivateMoneycardModal = ({args, closeModal}: PieActivateMoneycardModalProps) => {
  const {t} = getLocalizationService();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [cardNumber, setCardNumber] = useState<string[]>([]);
  const [error, setError] = useState(false);

  const onSubmit = async () => {
    trackEvent('mcActivate_submit');
    const activate = async () => {
      await apiService.activateMoneycard({
        fullMoneycardNumber: cardNumber.join(''),
        type: 'default',
      });
    };

    try {
      setIsLoading(true);
      await activate();
      if (args.onSuccess) {
        await args.onSuccess();
      }
      dispatch(setCurrentModal('moneycardFeedbackModal', {
        imgSrc: activateCardSuccessImg,
        imgAlt: t('success'),
        header: t('10bis_card_xxx_successfully_activated', {cardLastDigits: cardNumber[3]}),
        subHeader: t('your_old_card_has_been_canceled_and_can_no_longer_be_used'),
        primaryButton: {
          label: t('close_2'),
          onClick: closeModal,
        },
      }));
    } catch (err) {
      if (is401Error(err)) {
        await handleRefreshToken(err, onSubmit);
        return;
      }
      logger.warn('error while activating moneycard');
      dispatch(setCurrentModal('moneycardFeedbackModal', {
        imgSrc: frustratedImg,
        imgAlt: t('failure'),
        header: t('there_was_a_problem_the_card_wasnt_activated'),
        subHeader: t('it_seems_something_went_wrong_you_can_try_again_later'),
        primaryButton: {
          label: t('close_2'),
          onClick: closeModal,
        },
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Root>
      <Title id="modal-title">{t('activate_card2')}</Title>
      <StyledPieCloseModalButton onClick={closeModal} />
      <Content>
        <Description>{t('enter_the_card_number_you_have')}</Description>
        <PieCreditCardNumberInput
          cardInputError={error}
          setCardInputError={setError}
          cardNumber={cardNumber}
          setCardNumber={setCardNumber}
        />
        <SubmitButton
          variant="primaryLarge"
          disabled={cardNumber.join('').length !== 16}
          onClick={onSubmit}
          isLoading={isLoading}
        >
          {t('activate_card2')}
        </SubmitButton>
      </Content>
    </Root>
  );
};

PieActivateMoneycardModal.hideCloseButton = true;
PieActivateMoneycardModal.modalType = 'autoWidth';
attachModal('pieActivateMoneycardModal', PieActivateMoneycardModal);

export default PieActivateMoneycardModal;
